// useBucketNav — tracks which Standup bucket the user came from and exposes
// prev/next siblings so Command Center can wire J/K + topbar arrows without
// re-fetching the whole bucket.
//
// Usage:
//   From Standup DecideCard: navigate(`/thread/${id}`, { state: { bucket: 'needsYou', ids: [...] } })
//   From Center: const { prev, next, position } = useBucketNav(currentId)
//
// Falls back gracefully when there's no bucket context (direct URL entry,
// refresh): position = null, prev/next return nothing.

import { useLocation } from 'react-router-dom';

export type BucketKey = 'needsYou' | 'followups' | 'snoozed' | 'autoHandled' | 'drafts';

export interface BucketNavState {
  bucket: BucketKey;
  ids: string[];
  label?: string;
}

export interface BucketNavResult {
  bucket: BucketKey | null;
  bucketLabel: string | null;
  position: { index: number; total: number } | null;
  prevId: string | null;
  nextId: string | null;
  remainingIds: string[];
}

const BUCKET_LABEL: Record<BucketKey, string> = {
  needsYou: 'Needs you',
  followups: 'Circle back',
  snoozed: 'Snoozed',
  autoHandled: 'For the record',
  drafts: 'Drafts ready',
};

export function useBucketNav(currentId: string | undefined): BucketNavResult {
  const location = useLocation();
  const state = (location.state ?? null) as Partial<BucketNavState> | null;

  if (!state || !state.bucket || !state.ids || !currentId) {
    return {
      bucket: null,
      bucketLabel: null,
      position: null,
      prevId: null,
      nextId: null,
      remainingIds: [],
    };
  }

  const idx = state.ids.indexOf(currentId);
  if (idx === -1) {
    return {
      bucket: state.bucket,
      bucketLabel: state.label ?? BUCKET_LABEL[state.bucket],
      position: null,
      prevId: null,
      nextId: null,
      remainingIds: state.ids,
    };
  }

  return {
    bucket: state.bucket,
    bucketLabel: state.label ?? BUCKET_LABEL[state.bucket],
    position: { index: idx + 1, total: state.ids.length },
    prevId: idx > 0 ? state.ids[idx - 1]! : null,
    nextId: idx < state.ids.length - 1 ? state.ids[idx + 1]! : null,
    remainingIds: state.ids,
  };
}
